import React from 'react';
import { FiArrowUp, FiArrowDown, FiHelpCircle, FiUser, FiUsers } from 'react-icons/fi';
import LoadingSkeleton from './LoadingSkeleton';
import EmptyState from './EmptyState';

const StatsTable = ({
  data,
  loading,
  sortConfig,
  onSort,
  playerSearch,
  comparePlayers,
  onPlayerClick,
  onToggleComparison,
  playerImages = {},
  statsView = 'avg',
}) => {
  const filterBySearch = (dataArray) => {
    if (!playerSearch.trim()) return dataArray;
    return dataArray.filter(item => 
      item.Player?.toLowerCase().includes(playerSearch.toLowerCase())
    );
  };

  const getSortedData = (dataArray) => {
    if (!sortConfig.key) return dataArray;

    const getCellValue = (row, key) => {
      if (statsView === 'totals') {
        if (key === 'Score') return Number(row.totalScore ?? row.Score ?? 0);
        if (key === 'K') return Number(row.totalK ?? row.K ?? 0);
        if (key === 'D') return Number(row.totalD ?? row.D ?? 0);
        if (key === 'A') return Number(row.totalA ?? row.A ?? 0);
        if (key === 'Impact') return Number(row.totalImpact ?? row.Impact ?? 0);
      }
      return Number(row[key] ?? 0);
    };

    const sorted = [...dataArray].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'KD') {
        aVal = a.D > 0 ? (a.K / a.D) : a.K;
        bVal = b.D > 0 ? (b.K / b.D) : b.K;
      }
      if (sortConfig.key === 'winRate') {
        aVal = Number(a.winRate || 0);
        bVal = Number(b.winRate || 0);
      }
      if (['Score', 'K', 'D', 'A', 'Impact'].includes(sortConfig.key)) {
        aVal = getCellValue(a, sortConfig.key);
        bVal = getCellValue(b, sortConfig.key);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });

    return sorted;
  };

  const SortableHeader = ({ sortKey, label, tooltip, className = '' }) => (
    <div className={`stats-grid-header ${className}`}>
      <button className="sortable-header" onClick={() => onSort(sortKey)}>
        {label}
        {tooltip && <FiHelpCircle className="tooltip-icon" title={tooltip} />}
        {sortConfig.key === sortKey && (
          <span className="sort-indicator">
            {sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
          </span>
        )}
      </button>
    </div>
  );

  const sortedData = getSortedData(filterBySearch(data || []));
  const formatAvg = (value) => {
    const n = Number(value || 0);
    return Number.isFinite(n) ? n.toFixed(1) : '0.0';
  };

  return (
    <div className="map-performance-container">
      <div className="table-wrapper">
        <div className="stats-grid-container">
          {/* Header Row */}
          <div className="stats-grid-row stats-grid-header-row stats-grid-detailed">
            <SortableHeader sortKey="Player" label="Player" />
            <SortableHeader sortKey="Score" label="Score" />
            <SortableHeader sortKey="K" label="Kills" />
            <SortableHeader sortKey="D" label="Deaths" />
            <SortableHeader sortKey="A" label="Assists" />
            <SortableHeader 
              sortKey="Impact" 
              label="Impact" 
              tooltip="Overall impact score based on performance" 
            />
            <SortableHeader sortKey="winRate" label="Win %" tooltip="Win percentage" />
            <div className="stats-grid-header action-col">Actions</div>
          </div>

          {/* Data Rows */}
          {loading ? (
            <div className="stats-grid-row stats-grid-loading">
              <div className="stats-grid-full-width">
                <LoadingSkeleton />
              </div>
            </div>
          ) : sortedData.length === 0 ? (
            <div className="stats-grid-row stats-grid-empty">
              <div className="stats-grid-full-width">
                <EmptyState />
              </div>
            </div>
          ) : (
            sortedData.map((row, i) => {
              const isComparing = comparePlayers.includes(row.Player);
              
              return (
                <div 
                  key={i} 
                  className={`stats-grid-row ${isComparing ? 'comparing' : ''} stats-grid-detailed`}
                >
                  <div className="stats-grid-cell">
                    <div className="player-name-cell">
                      {playerImages[row.Player] && (
                        <img 
                          src={playerImages[row.Player]} 
                          alt={row.Player}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid #7E57C2',
                            marginRight: '8px'
                          }}
                        />
                      )}
                      <button 
                        className="player-name-link"
                        onClick={() => onPlayerClick(row.Player)}
                        title="Click to view player profile"
                      >
                        {row.Player}
                      </button>
                      <button
                        className={`compare-toggle ${isComparing ? 'active' : ''}`}
                        onClick={() => onToggleComparison(row.Player)}
                        title={isComparing ? 'Remove from comparison' : 'Add to comparison'}
                      >
                        <FiUsers size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="stats-grid-cell stats-grid-numeric">
                    <span className="stat-value">
                      {statsView === 'totals' ? (row.totalScore ?? row.Score ?? 0) : formatAvg(row.Score)}
                    </span>
                  </div>
                  <div className="stats-grid-cell stats-grid-numeric">
                    <span className="stat-value">{statsView === 'totals' ? (row.totalK ?? row.K ?? 0) : formatAvg(row.K)}</span>
                  </div>
                  <div className="stats-grid-cell stats-grid-numeric">{statsView === 'totals' ? (row.totalD ?? row.D ?? 0) : formatAvg(row.D)}</div>
                  <div className="stats-grid-cell stats-grid-numeric">{statsView === 'totals' ? (row.totalA ?? row.A ?? 0) : formatAvg(row.A)}</div>
                  <div className="stats-grid-cell stats-grid-numeric">{statsView === 'totals' ? (row.totalImpact ?? row.Impact ?? 0) : formatAvg(row.Impact)}</div>
                  <div className="stats-grid-cell stats-grid-numeric">{Math.round(Number(row.winRate || 0))}%</div>
                  <div className="stats-grid-cell action-col">
                    <button 
                      className="action-btn"
                      onClick={() => onPlayerClick(row.Player)}
                      title="View profile"
                    >
                      <FiUser size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsTable;

